import { crc32 } from '@/helpers/crc'
import type { TBrowserCache, TBrowserCacheEntry, TBrowserCacheOptions } from '@/types/browser-cache'

const storagePrefix = 'des-cache:'

function storageKeys(prefix: string): string[] {
  const keys: string[] = []

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key?.startsWith(prefix)) keys.push(key)
    }
  } catch {
    return []
  }

  return keys
}

function readEntry<T>(storageKey: string): TBrowserCacheEntry<T> | null {
  try {
    const entry = JSON.parse(localStorage.getItem(storageKey) ?? 'null')

    return entry && typeof entry.savedAt === 'number' && typeof entry.key === 'string' ? entry : null
  } catch {
    return null
  }
}

function removeKeys(keys: string[]) {
  try {
    keys.forEach((key) => localStorage.removeItem(key))
  } catch {
    return
  }
}

/**
 * Create a localStorage cache that keeps each value for `maxAgeMs` and at most `maxEntries` values, evicting the
 * oldest first. Bump `version` whenever the shape of the cached value changes. `keep` decides which loaded values
 * are worth storing; the rest are returned without being cached. Concurrent loads of one key share a single request.
 * An update changes a stored value but keeps the time it was first saved.
 */
export function createBrowserCache<T>(options: TBrowserCacheOptions<T>): TBrowserCache<T> {
  const namePrefix = `${storagePrefix}${options.name}-v`
  const prefix = `${namePrefix}${options.version}:`
  const now = options.now ?? Date.now
  const keep = options.keep ?? (() => true)
  const pending = new Map<string, Promise<T>>()

  removeKeys(storageKeys(namePrefix).filter((key) => !key.startsWith(prefix)))

  function storageKey(key: string): string {
    return prefix + crc32(key).toString(16)
  }

  function get(key: string): T | null {
    const entry = readEntry<T>(storageKey(key))

    if (!entry || entry.key !== key) return null

    if (now() - entry.savedAt > options.maxAgeMs) {
      removeKeys([storageKey(key)])

      return null
    }

    return entry.value
  }

  function evictOldest() {
    const entries = storageKeys(prefix).map((key) => ({ key, savedAt: readEntry(key)?.savedAt ?? 0 }))

    if (entries.length <= options.maxEntries) return

    entries.sort((a, b) => a.savedAt - b.savedAt)
    removeKeys(entries.slice(0, entries.length - options.maxEntries).map((entry) => entry.key))
  }

  function set(key: string, value: T, savedAt = now()) {
    const serialized = JSON.stringify({ key, savedAt, value } satisfies TBrowserCacheEntry<T>)

    try {
      localStorage.setItem(storageKey(key), serialized)
    } catch {
      removeKeys(storageKeys(prefix))

      try {
        localStorage.setItem(storageKey(key), serialized)
      } catch {
        return
      }
    }

    evictOldest()
  }

  function remember(key: string, load: () => Promise<T>): Promise<T> {
    const cached = get(key)

    if (cached !== null) return Promise.resolve(cached)

    const inFlight = pending.get(key)

    if (inFlight) return inFlight

    const request = load()
      .then((value) => {
        if (value !== null && keep(value)) set(key, value)

        return value
      })
      .finally(() => pending.delete(key))

    pending.set(key, request)

    return request
  }

  function update(key: string, change: (value: T) => T) {
    const value = get(key)

    if (value === null) return

    const entry = readEntry<T>(storageKey(key))

    if (entry) set(key, change(value), entry.savedAt)
  }

  return { get, remember, update }
}
